import { logError } from '@/lib/sentry.lib';
import { TELEMETRY_LOGS, logTelemetry } from '@/lib/telemetry.lib';

import { useParams } from 'react-router';

import { EMPTY_PENDING_SUBMISSIONS, useAssetEditStore } from '@/store/asset-edit.store';
import { API } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { type AssetDataType, type AssetType, assetTypes, isAssetType } from '@repo/types/asset';
import type { ModifyAssetPaymentParams } from '@repo/types/asset-payment';
import type { DraftAsset } from '@repo/types/draft';
import { type OrderAsset, isOrder } from '@repo/types/order';
import type { QuoteAsset } from '@repo/types/quotation';
import { getNotifier } from '@repo/utils';
import { invariant, isString } from '@repo/utils/type.utils';
import { type UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    deleteAttachment,
    getBarCode,
    getDefaultPrinter,
    getDraft,
    getFavouritePrinterList,
    getOrder,
    getPrinters,
    getQuotation,
    modifyOrderPaymentDate,
    updateAttachmentComment,
    uploadAttachment,
} from '@/api/asset.api';
import { projectOrderUpdateCommandsOnOrderAsset } from '@/commands/order-update.utils';

interface UseAssetOptions {
    enabled?: boolean;
    includePendingChanges?: boolean;
}

interface UsePrinterOptions {
    enabled?: boolean;
}

/**
 * Custom hook to retrieve and validate asset parameters from the URL.
 *
 * @returns An object containing the asset ID and asset type.
 *
 * @throws Will throw an error if the asset type is invalid or if the asset ID is missing.
 */
export const useAssetParams = () => {
    const { assetId, assetType } = useParams();
    invariant(isAssetType(assetType), `Invalid asset type: ${assetType}`);
    invariant(isString(assetId), 'Missing asset id');
    return { assetId, assetType };
};

export const useOrder = (assetId: string, withRetry?: boolean) =>
    useQuery({
        queryKey: [API.assetDetails, assetTypes.order, assetId],
        queryFn: () => getOrder(assetId),
        retry: withRetry ? 10 : false,
        retryDelay: withRetry ? 4000 : 0,
        meta: {
            persist: true,
        },
    });

export function useAsset(assetType: 'order', assetId: string, options?: UseAssetOptions): UseQueryResult<OrderAsset>;
export function useAsset(assetType: 'quote', assetId: string, options?: UseAssetOptions): UseQueryResult<QuoteAsset>;
export function useAsset(assetType: 'draft', assetId: string, options?: UseAssetOptions): UseQueryResult<DraftAsset>;
export function useAsset(
    assetType: AssetType,
    assetId: string,
    options?: UseAssetOptions
): UseQueryResult<AssetDataType>;
export function useAsset(assetType: AssetType, assetId: string, options?: UseAssetOptions) {
    const pendingSubmissions = useAssetEditStore((state) =>
        state.assetId === assetId ? state.pendingSubmissions : EMPTY_PENDING_SUBMISSIONS
    );
    const queryResult = useQuery({
        queryKey: [API.assetDetails, assetType, assetId],
        queryFn: async () => {
            if (assetType === 'order') {
                return await getOrder(assetId);
            }
            if (assetType === 'quote') {
                return await getQuotation(assetId);
            }
            return await getDraft(assetId);
        },
        enabled: options?.enabled ?? true,
        meta: {
            persist: true,
        },
    });

    if (options?.includePendingChanges === false) {
        return queryResult;
    }

    const orderUpdateCommands = pendingSubmissions.map((pendingSubmission) => pendingSubmission.submission.payload);
    const data =
        queryResult.data && assetType === assetTypes.order && isOrder(queryResult.data)
            ? projectOrderUpdateCommandsOnOrderAsset(queryResult.data, orderUpdateCommands)
            : queryResult.data;

    return Object.assign({}, queryResult, {
        data,
    });
}

export const usePrinters = (buCode: string, options?: UsePrinterOptions) =>
    useQuery({
        queryKey: [API.printers, buCode],
        queryFn: () => getPrinters(buCode),
        enabled: options?.enabled ?? true,
        meta: {
            persist: buCode.length > 0,
        },
    });

export const useFavouritePrinterList = (buCode: string, mailNickname: string, options?: UsePrinterOptions) =>
    useQuery({
        queryKey: [API.favouritePrinterList, buCode, mailNickname],
        queryFn: () => getFavouritePrinterList(buCode, mailNickname),
        enabled: options?.enabled ?? true,
        meta: {
            persist: buCode.length > 0 && mailNickname.length > 0,
        },
    });

export const useDefaultPrinter = (buCode: string, mailNickname: string, options?: UsePrinterOptions) =>
    useQuery({
        queryKey: [API.defaultPrinter, buCode, mailNickname],
        queryFn: () => getDefaultPrinter(buCode, mailNickname),
        enabled: options?.enabled ?? true,
        meta: {
            persist: buCode.length > 0 && mailNickname.length > 0,
        },
    });

export const useBarCode = (assetId: string) =>
    useQuery({
        queryKey: [API.barCode, assetId],
        queryFn: () => getBarCode(assetId),
    });

export const useUploadAttachment = (assetType: AssetType, assetId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: uploadAttachment,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [API.assetDetails, assetType, assetId] });
        },
        onError: (error) => {
            logError(error);
            const notifier = getNotifier();
            notifier?.add({
                title: translate('attachments_upload_notification_error_title'),
                message: translate('attachments_upload_notification_error_body'),
                type: 'error',
            });
        },
    });
};

export const useDeleteAttachment = (assetType: AssetType, assetId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteAttachment,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [API.assetDetails, assetType, assetId] });
        },
        onError: (error) => {
            logError(error);
            const notifier = getNotifier();
            notifier?.add({
                title: translate('attachments_delete_notification_error_title'),
                message: translate('attachments_delete_notification_error_body'),
                type: 'error',
            });
        },
    });
};

export const useUpdateAttachmentComment = (assetType: AssetType, assetId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateAttachmentComment,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [API.assetDetails, assetType, assetId] });
        },
        onError: (error) => {
            logError(error);
            const notifier = getNotifier();
            notifier?.add({
                title: translate('attachments_comment_notification_error_title'),
                message: translate('attachments_comment_notification_error_body'),
                type: 'error',
            });
        },
    });
};

export const useModifyOrderPaymentDate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (params: ModifyAssetPaymentParams) => modifyOrderPaymentDate(params),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [API.assetDetails] });
            const notifier = getNotifier();
            notifier?.add({
                title: translate('asset_payment_date_change_notification_success_title'),
                message: translate('asset_payment_date_change_notification_success_body'),
                type: 'info',
            });
        },
        onError: (error: unknown, variables) => {
            logError(error);
            logTelemetry('error', TELEMETRY_LOGS.AssetPaymentDateChangeFailed, {
                assetPaymentDateChange: {
                    orderNoSource: variables.orderNoSource,
                    paymentDate: variables.paymentDate,
                },
            });
            const notifier = getNotifier();
            notifier?.add({
                title: translate('asset_payment_date_change_notification_error_title'),
                message: translate('asset_payment_date_change_notification_error_body'),
                type: 'error',
            });
        },
    });
};
